// RETURNING-clause column extractor for DML-in-CTE bodies.
//
// PostgreSQL / SQLite / MariaDB allow INSERT/UPDATE/DELETE/MERGE statements
// inside a `WITH foo AS (…)` body, with an optional `RETURNING <expr-list>`
// at the end. When such a CTE has no explicit column list (`WITH foo (a, b)
// AS (…)`), the resolver still wants to know the CTE's output columns so
// `SELECT foo.|` completion can surface them.
//
// The extractor walks the CTE body's tokens at top-level paren depth (0)
// looking for a `RETURNING` keyword, then parses the comma-separated column
// list following it. For each segment, the column name resolution priority
// is:
//   1. `expr AS alias` at depth 0 → the alias.
//   2. Otherwise the LAST identifier at depth 0 — handles bare `col` and
//      qualified `t.col` (the trailing ident wins).
//   3. Expressions whose result is not an identifier (`UPPER(name)` without
//      AS) yield no column name and are skipped.

import type { Token } from '../context/tokenizer';

import { parseIdentifierToken } from './identifierUtils';

/**
 * Walk `tokens` from `bodyStart + 1` to `bodyEnd - 1` and find a top-level
 * `RETURNING` keyword. Returns the comma-separated column names after
 * RETURNING, or `undefined` when no RETURNING clause sits at depth 0 inside
 * the body. `bodyStart` is the index of the body's opening `(` and
 * `bodyEnd` is the index of its matching `)`.
 */
export function extractReturningColumns(
  tokens: readonly Token[],
  bodyStart: number,
  bodyEnd: number,
): readonly string[] | undefined {
  let depth = 0;
  for (let i = bodyStart + 1; i < bodyEnd; i += 1) {
    const tok = tokens[i];
    if (tok.type === 'lparen') depth += 1;
    else if (tok.type === 'rparen') depth -= 1;
    else if (
      depth === 0 &&
      tok.type === 'kw' &&
      tok.text.toUpperCase() === 'RETURNING'
    ) {
      return parseReturningList(tokens, i + 1, bodyEnd);
    }
  }
  return undefined;
}

function parseReturningList(
  tokens: readonly Token[],
  start: number,
  end: number,
): string[] {
  const cols: string[] = [];
  let segStart = start;
  let depth = 0;
  for (let i = start; i <= end; i += 1) {
    const tok = i < end ? tokens[i] : null;
    if (tok) {
      if (tok.type === 'lparen') depth += 1;
      else if (tok.type === 'rparen') depth -= 1;
    }
    const atSegmentEnd = !tok || (depth === 0 && tok.type === 'comma');
    if (atSegmentEnd) {
      const colName = pickColumnNameFromSegment(tokens, segStart, i);
      if (colName) cols.push(colName);
      segStart = i + 1;
    }
    if (!tok) break;
  }
  return cols;
}

function pickColumnNameFromSegment(
  tokens: readonly Token[],
  start: number,
  end: number,
): string | null {
  // Pass 1: look for `AS <ident>` at depth 0 — alias wins.
  let depth = 0;
  for (let i = start; i < end; i += 1) {
    const tok = tokens[i];
    if (tok.type === 'lparen') {
      depth += 1;
      continue;
    }
    if (tok.type === 'rparen') {
      depth -= 1;
      continue;
    }
    if (
      depth === 0 &&
      tok.type === 'kw' &&
      tok.text.toUpperCase() === 'AS' &&
      i + 1 < end
    ) {
      const next = tokens[i + 1];
      if (next.type === 'ident' || next.type === 'kw') {
        return parseIdentifierToken(next.text).content;
      }
    }
  }
  // Pass 2: take the LAST identifier at depth 0 in the segment. Handles
  // bare `col` and qualified `t.col` (the trailing ident wins).
  let last: string | null = null;
  depth = 0;
  for (let i = start; i < end; i += 1) {
    const tok = tokens[i];
    if (tok.type === 'lparen') {
      depth += 1;
      continue;
    }
    if (tok.type === 'rparen') {
      depth -= 1;
      continue;
    }
    if (depth !== 0) continue;
    if (tok.type === 'ident' || tok.type === 'kw') {
      const upper = tok.text.toUpperCase();
      if (upper === 'AS' || upper === 'DISTINCT') continue;
      last = parseIdentifierToken(tok.text).content;
    }
  }
  return last;
}
